import { useState } from 'react';
import { GripVertical, Trash2, Plus } from 'lucide-react';

export interface EditableSection {
  name: string;
  fixed_prefix: string;
  normal_default: string;
  slot_name: string;
  conditional: boolean;
  include: boolean;
}

interface Props {
  sections: EditableSection[];
  setSections: (s: EditableSection[]) => void;
}

/**
 * Drag-to-reorder section builder. The radiologist arranges the report's
 * sections (titles) to suit their layout, edits each section's heading and
 * canonical normal text, toggles inclusion / optional. Native HTML5 drag and
 * drop — no extra dependencies.
 */
export default function SectionBuilder({ sections, setSections }: Props) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const update = (i: number, patch: Partial<EditableSection>) => {
    setSections(sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };

  const remove = (i: number) => setSections(sections.filter((_, idx) => idx !== i));

  const addSection = () => {
    setSections([
      ...sections,
      {
        name: 'New Section',
        fixed_prefix: '',
        normal_default: '',
        slot_name: `section_${sections.length + 1}`,
        conditional: false,
        include: true,
      },
    ]);
  };

  const reorder = (from: number, to: number) => {
    if (from === to) return;
    const next = [...sections];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSections(next);
  };

  return (
    <div className="space-y-2">
      {sections.map((s, i) => (
        <div
          key={i}
          onDragOver={(e) => {
            e.preventDefault();
            setOverIndex(i);
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (dragIndex !== null) reorder(dragIndex, i);
            setDragIndex(null);
            setOverIndex(null);
          }}
          className={`rounded-lg border p-3 transition-colors ${
            overIndex === i && dragIndex !== null
              ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
              : 'border-gray-200 dark:border-surface-800 bg-white dark:bg-surface-900'
          } ${dragIndex === i ? 'opacity-40' : ''} ${!s.include ? 'opacity-60' : ''}`}
        >
          <div className="flex items-center gap-2">
            {/* Drag handle */}
            <button
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
              title="Drag to reorder"
              className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
            >
              <GripVertical size={18} />
            </button>

            {/* Include toggle */}
            <input
              type="checkbox"
              checked={s.include}
              onChange={(e) => update(i, { include: e.target.checked })}
              title="Include this section"
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 shrink-0"
            />

            {/* Heading */}
            <input
              value={s.name}
              onChange={(e) => update(i, { name: e.target.value })}
              placeholder="Section heading"
              className="flex-1 min-w-0 px-2 py-1 text-sm font-medium bg-gray-50 dark:bg-surface-850 border border-gray-200 dark:border-surface-800 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            />

            <label className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 shrink-0">
              <input
                type="checkbox"
                checked={s.conditional}
                onChange={(e) => update(i, { conditional: e.target.checked })}
                className="w-3.5 h-3.5"
              />
              optional
            </label>

            <button
              onClick={() => remove(i)}
              title="Remove section"
              className="text-gray-400 hover:text-red-500 shrink-0"
            >
              <Trash2 size={15} />
            </button>
          </div>

          {/* Canonical normal text */}
          <textarea
            value={s.normal_default}
            onChange={(e) => update(i, { normal_default: e.target.value })}
            placeholder="Canonical normal text for this section (used verbatim when nothing is dictated for it)…"
            rows={2}
            className="mt-2 w-full px-2 py-1.5 text-sm bg-gray-50 dark:bg-surface-850 border border-gray-200 dark:border-surface-800 rounded text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
          />
        </div>
      ))}

      <button
        onClick={addSection}
        className="flex items-center gap-1.5 px-3 py-2 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
      >
        <Plus size={15} />
        Add section
      </button>
    </div>
  );
}
